import { useCallback } from 'react';
import './led-pill-toggle.css';

/**
 * Props for the LedPillToggle component
 */
interface LedPillToggleProps {
  /** Label for the left position (for accessibility) */
  leftLabel: string;
  /** Label for the right position (for accessibility) */
  rightLabel: string;
  /** Current active position */
  activePosition: 'left' | 'right';
  /** Callback when toggle position changes */
  onToggle: (position: 'left' | 'right') => void;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Additional CSS class */
  className?: string;
}

/**
 * LedPillToggle Component
 *
 * A pill-shaped toggle with LED indicator lights on each end.
 * The slider moves between left and right positions, with the
 * corresponding LED lighting up to indicate the active state.
 *
 * Features:
 * - Automotive dashboard aesthetic
 * - LED glow effect on active side
 * - Smooth sliding animation
 * - Keyboard accessible
 * - Screen reader support
 */
export function LedPillToggle({
  leftLabel,
  rightLabel,
  activePosition,
  onToggle,
  size = 'medium',
  className = '',
}: LedPillToggleProps): JSX.Element {
  const handleClick = useCallback(() => {
    onToggle(activePosition === 'left' ? 'right' : 'left');
  }, [activePosition, onToggle]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleClick();
      } else if (event.key === 'ArrowLeft' && activePosition === 'right') {
        event.preventDefault();
        onToggle('left');
      } else if (event.key === 'ArrowRight' && activePosition === 'left') {
        event.preventDefault();
        onToggle('right');
      }
    },
    [activePosition, handleClick, onToggle]
  );

  const pillClasses = [
    'led-pill',
    'led-pill--' + size,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const leftLedClasses = [
    'led-pill__led',
    'led-pill__led--left',
    activePosition === 'left' ? 'led-pill__led--active' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const rightLedClasses = [
    'led-pill__led',
    'led-pill__led--right',
    activePosition === 'right' ? 'led-pill__led--active' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const ariaLabel = 'Toggle between ' + leftLabel + ' and ' + rightLabel;
  const srText = (activePosition === 'left' ? leftLabel : rightLabel) + ' selected';

  return (
    <div
      className={pillClasses}
      data-position={activePosition}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="switch"
      aria-checked={activePosition === 'right'}
      aria-label={ariaLabel}
      tabIndex={0}
    >
      {/* Left LED indicator */}
      <span
        className={leftLedClasses}
        aria-hidden="true"
      />

      {/* Sliding indicator */}
      <span className="led-pill__slider" aria-hidden="true" />

      {/* Right LED indicator */}
      <span
        className={rightLedClasses}
        aria-hidden="true"
      />

      {/* Screen reader text */}
      <span className="led-pill__sr-only">
        {srText}
      </span>
    </div>
  );
}

export default LedPillToggle;
